/**
 * Changes the person makes to how transactions are counted, kept apart from
 * the extracted rows so re-reading a statement never loses them.
 *
 * - `Corrections`: one transaction, by txn_key (account, date, amount and
 *   occurrence; see withTxnKeys) — a new category, or "one-off". Rows saved
 *   before txn_key existed are matched by txn_id.
 * - `Rules`: every transaction whose merchant or description contains `match`
 *   (optionally on one account) — now and in future statements.
 *
 * A correction beats a rule; a later rule beats an earlier one. "One-off"
 * marks real spending that should not count towards the monthly averages and
 * savings rate (a car bought outright). See ARCHITECTURE.md, Corrections.
 */
import { formatTimestamp } from './dates'
import { TILLER_SOURCE } from './duplicates'
import { accountKey } from './filing'
import type { Database, Row } from './google/sheets'

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim())
const yes = (v: unknown) => ['yes', 'true', '1', 'y'].includes(str(v).toLowerCase())

export interface Rule {
  rule_id: string
  match: string
  /** Empty for any account. */
  account_id: string
  /** Empty to keep each transaction's own category. */
  category: string
  one_off: boolean
}

export function toRule(r: Row): Rule {
  return { rule_id: str(r.rule_id), match: str(r.match), account_id: str(r.account_id), category: str(r.category), one_off: yes(r.one_off) }
}

export function ruleMatches(rule: Rule, t: { merchant?: unknown; description?: unknown; account_id?: unknown }): boolean {
  if (!rule.match) return false
  if (rule.account_id && rule.account_id !== str(t.account_id)) return false
  return `${str(t.merchant)} ${str(t.description)}`.toLowerCase().includes(rule.match.toLowerCase())
}

/**
 * Each transaction with its counted category. Adds `extracted_category`,
 * `one_off` ('yes' or '') and `corrected_by` ('you', 'rule: <match>' or '').
 */
export function applyCorrections(transactions: Row[], corrections: Row[] = [], rules: Row[] = []): Row[] {
  const byKey = new Map(corrections.filter((c) => str(c.txn_key)).map((c) => [str(c.txn_key), c]))
  const byId = new Map(corrections.filter((c) => !str(c.txn_key)).map((c) => [str(c.txn_id), c]))
  const active = rules.map(toRule).filter((r) => r.match)
  return transactions.map((t) => {
    // Safe to apply twice: rows already corrected keep what they have.
    const out: Row = { ...t, extracted_category: t.extracted_category ?? t.category, corrected_by: t.corrected_by ?? '' }
    // A one-off the source marked (Tiller's car purchases) stays one: a rule's
    // unticked box means "not set", not "regular", so a rule can only add the
    // flag. Among rules the later one wins; a correction on the row decides last.
    let ruleOneOff = false
    for (const rule of active) {
      if (!ruleMatches(rule, t)) continue
      if (rule.category) out.category = rule.category
      ruleOneOff = rule.one_off
      out.corrected_by = `rule: ${rule.match}`
    }
    let oneOff = t.one_off === 'yes' || ruleOneOff
    const c = (str(t.txn_key) && byKey.get(str(t.txn_key))) || byId.get(str(t.txn_id))
    if (c) {
      if (str(c.category)) out.category = str(c.category)
      oneOff = yes(c.one_off)
      out.corrected_by = 'you'
      if (str(c.carried_from)) out.carried_from = str(c.carried_from)
    }
    out.one_off = oneOff ? 'yes' : ''
    return out
  })
}

export const isOneOff = (t: Row) => t.one_off === 'yes'

/** Days apart a Tiller row and the statement row that replaced it may be dated. */
export const CARRY_DAYS = 3

/**
 * Points each correction at the transaction it now belongs to.
 *
 * - One saved before txn_key existed gets the key of the transaction its
 *   txn_id names now, so it follows that transaction through de-duplication.
 * - One made on a Tiller row whose key no transaction has any more (a
 *   statement has since replaced the row, dated a day or two differently)
 *   moves to the statement row on the same account for the same amount, dated
 *   within CARRY_DAYS: the closest date, then the earliest. A row that has its
 *   own correction, or already took a carried one, is never taken. The moved
 *   correction keeps the key it is stored under as `carried_from`.
 */
export function keyCorrections(corrections: Row[] = [], keyed: Row[]): Row[] {
  const keyOf = new Map(keyed.map((t) => [str(t.txn_id), str(t.txn_key)]))
  const withKeys = corrections.map((c) => (str(c.txn_key) || !keyOf.get(str(c.txn_id)) ? c : { ...c, txn_key: keyOf.get(str(c.txn_id)) }))
  const live = new Set(keyed.map((t) => str(t.txn_key)))
  const taken = new Set(withKeys.map((c) => str(c.txn_key)).filter((k) => live.has(k)))
  const statements = keyed.filter((t) => str(t.source_file_id) !== TILLER_SOURCE)
  return withKeys.map((c) => {
    const key = str(c.txn_key)
    if (!str(c.txn_id).startsWith('tiller:') || live.has(key)) return c
    // account|date|amount|occurrence; the account id is everything before the last three parts.
    const parts = key.split('|')
    if (parts.length < 4) return c
    const [date, amount] = parts.slice(-3, -1)
    const account = accountKey(parts.slice(0, -3).join('|'))
    const at = Date.parse(date)
    if (!Number.isFinite(at)) return c
    const near = statements
      .filter((t) => !taken.has(str(t.txn_key)) && accountKey(str(t.account_id)) === account && Number(t.amount).toFixed(2) === amount)
      .map((t) => ({ t, days: Math.abs(Date.parse(str(t.date)) - at) / 86_400_000 }))
      .filter(({ days }) => days <= CARRY_DAYS)
      .sort((a, b) => a.days - b.days || str(a.t.date).localeCompare(str(b.t.date)))[0]
    if (!near) return c
    taken.add(str(near.t.txn_key))
    return { ...c, txn_key: str(near.t.txn_key), carried_from: key }
  })
}

export interface CorrectionTarget {
  txn_id: string
  txn_key: string
  /** The Tiller row's key a correction on this row is stored under, when it carried over. */
  carried_from?: string
}

export async function saveCorrection(db: Database, t: CorrectionTarget, change: { category: string; one_off: boolean }): Promise<void> {
  await db.upsert('Corrections', 'txn_key', [
    { txn_key: t.txn_key, txn_id: t.txn_id, category: change.category, one_off: change.one_off ? 'yes' : '', updated_at: formatTimestamp(new Date()) },
  ])
  // Saved under the statement row's own key now; the carried copy would only come back.
  if (t.carried_from) await db.replaceWhere('Corrections', 'txn_key', t.carried_from, [])
}

export async function clearCorrection(db: Database, t: CorrectionTarget): Promise<void> {
  await db.replaceWhere('Corrections', 'txn_key', t.txn_key, [])
  if (t.carried_from) await db.replaceWhere('Corrections', 'txn_key', t.carried_from, [])
  // A row saved before txn_key existed.
  await db.replaceWhere('Corrections', 'txn_id', t.txn_id, [])
}

export async function saveRule(db: Database, rule: Omit<Rule, 'rule_id'>): Promise<void> {
  const now = new Date()
  await db.upsert('Rules', 'rule_id', [
    {
      rule_id: `rule-${now.getTime()}`,
      match: rule.match,
      account_id: rule.account_id,
      category: rule.category,
      one_off: rule.one_off ? 'yes' : '',
      created_at: formatTimestamp(now),
    },
  ])
}

export async function deleteRule(db: Database, rule_id: string): Promise<void> {
  await db.replaceWhere('Rules', 'rule_id', rule_id, [])
}
